// src/components/fraud/FraudLogsFilter.js
// Filter bar for the Fraud Logs investigation page.
// Controls: Search | Action | Phase | Risk Level | Date range | Min Score | Reset

import React from 'react';
import { Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import { DEFAULT_FILTERS } from '../../hooks/useFraudLogs';

// ─── Shared select style ──────────────────────────────────────────────────────
const selectStyle = {
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  fontSize: 13,
  padding: '6px 10px',
  cursor: 'pointer',
  appearance: 'auto',
};

const inputStyle = {
  backgroundColor: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  fontSize: 13,
};

const labelStyle = {
  fontSize: 10,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--text-secondary)',
  marginBottom: 4,
  display: 'block',
};

// ─── Main Component ───────────────────────────────────────────────────────────
const FraudLogsFilter = ({ filters, setFilters, onApply, onReset, loading }) => {
  const isDirty = JSON.stringify(filters) !== JSON.stringify(DEFAULT_FILTERS);

  const set = (key, val) => setFilters((f) => ({ ...f, [key]: val }));

  // Apply on Enter key in search box
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') onApply();
  };

  return (
    <div
      id="fraud-logs-filter-bar"
      className="theme-card rounded-3 p-3 mb-4"
      style={{ borderColor: 'rgba(255,255,255,0.08)' }}
    >
      <Row className="g-2 align-items-end">

        {/* ── Search ─────────────────────────────────────────── */}
        <Col xs={12} md={5} lg={4}>
          <label style={labelStyle}>Search</label>
          <InputGroup>
            <Form.Select
              value={filters.searchField}
              onChange={(e) => set('searchField', e.target.value)}
              style={{
                ...selectStyle,
                maxWidth: 140,
                borderRadius: '8px 0 0 8px',
                borderRight: 'none',
              }}
              aria-label="Search field"
            >
              <option value="transactionId">Txn ID</option>
              <option value="uid">UID</option>
              <option value="userEmail">Email</option>
              <option value="destination">Destination</option>
            </Form.Select>
            <Form.Control
              id="fraud-logs-search-input"
              type="text"
              placeholder="Exact value…"
              value={filters.search}
              onChange={(e) => set('search', e.target.value)}
              onKeyDown={handleSearchKeyDown}
              style={{ ...inputStyle, borderRadius: '0 8px 8px 0', borderLeft: 'none' }}
            />
          </InputGroup>
        </Col>

        {/* ── Action ─────────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={1}>
          <label style={labelStyle}>Action</label>
          <Form.Select
            value={filters.action}
            onChange={(e) => set('action', e.target.value)}
            style={selectStyle}
            aria-label="Filter by action"
          >
            <option value="ALL">All</option>
            <option value="ALLOW">Allow</option>
            <option value="LOG">Log</option>
            <option value="REVIEW">Review</option>
            <option value="BLOCK">Block</option>
          </Form.Select>
        </Col>

        {/* ── Phase ──────────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={2}>
          <label style={labelStyle}>Phase</label>
          <Form.Select
            value={filters.phase}
            onChange={(e) => set('phase', e.target.value)}
            style={selectStyle}
            aria-label="Filter by phase"
          >
            <option value="ALL">All</option>
            <option value="pre_check">pre_check</option>
            <option value="webhook_analysis">webhook_analysis</option>
          </Form.Select>
        </Col>

        {/* ── Risk Level ─────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={2}>
          <label style={labelStyle}>Risk Level</label>
          <Form.Select
            value={filters.riskLevel}
            onChange={(e) => set('riskLevel', e.target.value)}
            style={selectStyle}
            aria-label="Filter by risk level"
          >
            <option value="ALL">All</option>
            <option value="normal">Normal</option>
            <option value="elevated">Elevated</option>
            <option value="highest">Highest</option>
          </Form.Select>
        </Col>

        {/* ── Date From ──────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={1}>
          <label style={labelStyle}>From</label>
          <Form.Control
            type="date"
            value={filters.dateFrom}
            onChange={(e) => set('dateFrom', e.target.value)}
            style={inputStyle}
            aria-label="Date from"
          />
        </Col>

        {/* ── Date To ────────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={1}>
          <label style={labelStyle}>To</label>
          <Form.Control
            type="date"
            value={filters.dateTo}
            onChange={(e) => set('dateTo', e.target.value)}
            style={inputStyle}
            aria-label="Date to"
          />
        </Col>

        {/* ── Min Score ──────────────────────────────────────── */}
        <Col xs={6} sm={4} md={2} lg={1}>
          <label style={labelStyle}>Min Score</label>
          <Form.Control
            type="number"
            min={0}
            max={100}
            placeholder="0–100"
            value={filters.minScore}
            onChange={(e) => set('minScore', e.target.value)}
            style={inputStyle}
            aria-label="Minimum fraud score"
          />
        </Col>

        {/* ── Actions ────────────────────────────────────────── */}
        <Col xs={12} sm="auto" className="d-flex gap-2 align-items-end">
          <Button
            id="fraud-logs-apply-btn"
            onClick={onApply}
            disabled={loading}
            style={{
              background: 'var(--accent-lime)',
              border: 'none',
              color: '#050014',
              fontWeight: 700,
              fontSize: 13,
              padding: '7px 18px',
              borderRadius: 8,
              whiteSpace: 'nowrap',
            }}
          >
            {loading ? (
              <span className="admin-loader-sm" style={{ borderTopColor: '#050014' }} />
            ) : (
              'Apply'
            )}
          </Button>
          {isDirty && (
            <Button
              id="fraud-logs-reset-btn"
              onClick={onReset}
              disabled={loading}
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.15)',
                color: 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: 13,
                padding: '7px 14px',
                borderRadius: 8,
                whiteSpace: 'nowrap',
              }}
            >
              Reset
            </Button>
          )}
        </Col>
      </Row>

      {/* ── Active filter pills ─────────────────────────────────────────────── */}
      {isDirty && (
        <div className="d-flex flex-wrap gap-2 mt-2">
          {filters.action !== 'ALL' && <FilterPill label={`Action: ${filters.action}`} onRemove={() => set('action', 'ALL')} />}
          {filters.phase !== 'ALL' && <FilterPill label={`Phase: ${filters.phase}`} onRemove={() => set('phase', 'ALL')} />}
          {filters.riskLevel !== 'ALL' && <FilterPill label={`Risk: ${filters.riskLevel}`} onRemove={() => set('riskLevel', 'ALL')} />}
          {filters.dateFrom && <FilterPill label={`From: ${filters.dateFrom}`} onRemove={() => set('dateFrom', '')} />}
          {filters.dateTo && <FilterPill label={`To: ${filters.dateTo}`} onRemove={() => set('dateTo', '')} />}
          {filters.minScore !== '' && <FilterPill label={`Score ≥ ${filters.minScore}`} onRemove={() => set('minScore', '')} />}
          {filters.search && <FilterPill label={`"${filters.search}"`} onRemove={() => { set('search', ''); set('searchField', 'transactionId'); }} />}
        </div>
      )}
    </div>
  );
};

// ─── Filter pill (active filter chip) ────────────────────────────────────────
const FilterPill = ({ label, onRemove }) => (
  <span
    style={{
      fontSize: 11,
      color: 'var(--accent-lime)',
      background: 'rgba(217,255,31,0.08)',
      border: '1px solid rgba(217,255,31,0.25)',
      borderRadius: 20,
      padding: '2px 8px',
      display: 'flex',
      alignItems: 'center',
      gap: 4,
    }}
  >
    {label}
    <button
      onClick={onRemove}
      style={{
        background: 'none',
        border: 'none',
        color: 'var(--text-secondary)',
        padding: 0,
        cursor: 'pointer',
        fontSize: 12,
        lineHeight: 1,
      }}
    >
      ×
    </button>
  </span>
);

export default FraudLogsFilter;
